/**
 * Предустановка: Простой проект с 5 коммитами
 * Используется для уроков по базовым командам Git
 */

import type { GitPreset } from './types';

/** Базовый проект с линейной историей коммитов */
export const simpleProjectPreset: GitPreset = {
  id: 'simple-project',
  name: 'Простой проект',
  description: 'Базовый проект с 5 коммитами для изучения базовых команд Git',

  currentBranch: 'main',

  branches: [{ name: 'main', head: 'c5', color: '#F05032' }],

  commits: [
    {
      sha: 'c1',
      message: 'Initial commit: Add README.md',
      parents: [],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704067200, // 2024-01-01
      files: ['README.md'],
    },
    {
      sha: 'c2',
      message: 'Add project structure',
      parents: ['c1'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704153600, // 2024-01-02
      files: ['src/index.ts', 'src/utils.ts', 'package.json'],
    },
    {
      sha: 'c3',
      message: 'Implement core functionality',
      parents: ['c2'],
      author: 'Maria Ivanova',
      authorEmail: 'maria@example.com',
      timestamp: 1704240000, // 2024-01-03
      files: ['src/core.ts', 'src/types.ts'],
    },
    {
      sha: 'c4',
      message: 'Add configuration files',
      parents: ['c3'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704326400, // 2024-01-04
      files: ['tsconfig.json', '.gitignore', '.eslintrc.json'],
    },
    {
      sha: 'c5',
      message: 'Update README with usage examples',
      parents: ['c4'],
      author: 'Maria Ivanova',
      authorEmail: 'maria@example.com',
      timestamp: 1704412800, // 2024-01-05
      files: ['README.md'],
    },
  ],

  files: [
    {
      path: 'README.md',
      content: `# My Awesome Project

Учебный проект для изучения Git.

## Установка

\`\`\`bash
npm install
\`\`\`

## Использование

\`\`\`bash
npm start
\`\`\`

## Функции

- \`greet(name)\` - приветствие пользователя
- \`calculate(a, b)\` - математические операции

## Авторы

- Ivan Petrov
- Maria Ivanova
`,
      status: 'committed',
    },
    {
      path: 'src/index.ts',
      content: `import { greet } from './utils';
import { calculate } from './core';

console.log(greet('World'));
console.log('Result:', calculate(5, 3));
`,
      status: 'committed',
    },
    {
      path: 'src/utils.ts',
      content: `/**
 * Утилиты проекта
 */

export function greet(name: string): string {
  return \`Hello, \${name}!\`;
}

export function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}
`,
      status: 'committed',
    },
    {
      path: 'src/core.ts',
      content: `/**
 * Основная логика проекта
 */

import type { Operation } from './types';

export function calculate(a: number, b: number, op: Operation = 'add'): number {
  switch (op) {
    case 'add':
      return a + b;
    case 'subtract':
      return a - b;
    case 'multiply':
      return a * b;
    case 'divide':
      return b !== 0 ? a / b : NaN;
    default:
      throw new Error(\`Unknown operation: \${op}\`);
  }
}
`,
      status: 'committed',
    },
    {
      path: 'src/types.ts',
      content: `/**
 * Типы проекта
 */

export type Operation = 'add' | 'subtract' | 'multiply' | 'divide';

export interface Config {
  name: string;
  version: string;
  debug: boolean;
}
`,
      status: 'committed',
    },
    {
      path: 'package.json',
      content: `{
  "name": "my-awesome-project",
  "version": "1.0.0",
  "description": "Учебный проект для изучения Git",
  "main": "src/index.ts",
  "scripts": {
    "start": "ts-node src/index.ts",
    "build": "tsc",
    "test": "jest"
  },
  "keywords": ["git", "tutorial", "learning"],
  "author": "Ivan Petrov",
  "license": "MIT"
}
`,
      status: 'committed',
    },
    {
      path: 'tsconfig.json',
      content: `{
  "compilerOptions": {
    "target": "ES2020",
    "module": "commonjs",
    "strict": true,
    "esModuleInterop": true,
    "outDir": "./dist",
    "rootDir": "./src"
  },
  "include": ["src/**/*"],
  "exclude": ["node_modules"]
}
`,
      status: 'committed',
    },
    {
      path: '.gitignore',
      content: `# Dependencies
node_modules/

# Build output
dist/
build/

# IDE
.idea/
.vscode/
*.swp
*.swo

# OS
.DS_Store
Thumbs.db

# Logs
*.log
npm-debug.log*
`,
      status: 'committed',
    },
    {
      path: '.eslintrc.json',
      content: `{
  "root": true,
  "parser": "@typescript-eslint/parser",
  "plugins": ["@typescript-eslint"],
  "extends": [
    "eslint:recommended",
    "plugin:@typescript-eslint/recommended"
  ],
  "rules": {
    "no-console": "warn",
    "@typescript-eslint/no-unused-vars": "error"
  }
}
`,
      status: 'committed',
    },
  ],

  visualization: {
    branchColors: {
      main: '#F05032',
    },
    nodePositions: {
      c1: { x: 100, y: 200 },
      c2: { x: 200, y: 200 },
      c3: { x: 300, y: 200 },
      c4: { x: 400, y: 200 },
      c5: { x: 500, y: 200 },
    },
    highlightedCommits: ['c5'],
  },
};
